/**
 * Field operations service.
 * Provides async functions for field-related operations.
 */

import { onlineManager, QueryClient } from "@tanstack/react-query";
import { queryClient } from "common/queryClient";
import { queryKeys } from "common/queryKeys";
import { fetchApi } from "common/utils/Api";
import { ArableField, Field } from "model";
import type { FeatureCollection } from "geojson";

/**
 * Get tiles for a field from cache.
 * Checks both Field and ArableField caches.
 */
function getTilesFromCache(fieldGuid: string): string[] | undefined {
  const field = queryClient.getQueryData<Field>(
    queryKeys.fields.detail(fieldGuid)
  );
  const arableField = queryClient.getQueryData<ArableField>(
    queryKeys.arableFields.detail(fieldGuid)
  );

  return field?.tiles || arableField?.tiles;
}

/**
 * Clear tile metadata from React Query cache and drop stale tile guids from the
 * cached field record so map layers do not refetch deleted tile ids.
 * Use this when field is DELETED (backend auto-deletes tiles).
 */
export function clearTilesFromCache(
  fieldGuid: string,
  qc: QueryClient = queryClient
): void {
  const tiles = getTilesFromCache(fieldGuid);

  if (tiles && tiles.length > 0) {
    tiles.forEach((tileId) => {
      qc.removeQueries({ queryKey: queryKeys.tiles.detail(tileId) });
    });
  }

  const invalidatedTimestamp = Date.now() / 1000;
  const currentField = qc.getQueryData<Field>(
    queryKeys.fields.detail(fieldGuid)
  );
  if (currentField) {
    qc.setQueryData<Field>(queryKeys.fields.detail(fieldGuid), {
      ...currentField,
      invalidatedTimestamp,
      tiles: [],
    });
  }

  const currentArableField = qc.getQueryData<ArableField>(
    queryKeys.arableFields.detail(fieldGuid)
  );
  if (currentArableField) {
    qc.setQueryData<ArableField>(queryKeys.arableFields.detail(fieldGuid), {
      ...currentArableField,
      invalidatedTimestamp,
      tiles: [],
    });
  }
}

/**
 * Delete tiles via backend API.
 * Use this when field geometry CHANGES (old tiles invalid, backend needs to regenerate).
 */
export async function deleteTilesViaApi(fieldGuid: string): Promise<void> {
  const tiles = getTilesFromCache(fieldGuid);

  if (tiles && tiles.length > 0) {
    await Promise.all(
      tiles.map((tileId) =>
        fetchApi(`tiles/${tileId}`, { method: "DELETE" }).catch((err) => {
          // Ignore 404 errors (tile already deleted)
          if (err?.message !== "Not Found") {
            console.error(`Failed to delete tile ${tileId}:`, err);
          }
        })
      )
    );
  }
}

/**
 * Delete all tiles for a field (both cache and backend).
 * Use this when field geometry CHANGES and tiles need to be regenerated.
 *
 * Backend DELETE calls are skipped while offline — only the local cache is
 * cleared. Callers that run after a confirmed server write (e.g. the move
 * mutation's onSuccess) should only invoke this once the device is online.
 *
 * For field DELETION, use clearTilesFromCache() instead (backend auto-deletes).
 */
export async function deleteAllTilesForField(fieldGuid: string): Promise<void> {
  if (onlineManager.isOnline()) {
    await deleteTilesViaApi(fieldGuid);
  }
  clearTilesFromCache(fieldGuid);
}

/**
 * Invalidate field data in React Query cache.
 * Sets invalidatedTimestamp and clears tiles array so React Query's refetchInterval
 * will poll for new tiles. Also triggers a refetch.
 * @param fieldGuid - The field guid to invalidate
 * @param queryClient - React Query client instance (optional - uses global if not provided)
 */
export function invalidateFieldData(
  fieldGuid: string,
  queryClientParam?: QueryClient
): void {
  const qc = queryClientParam ?? queryClient;

  // Get current cached field data
  const currentField = qc.getQueryData<Field>(
    queryKeys.fields.detail(fieldGuid)
  );

  if (currentField) {
    // Set invalidatedTimestamp and clear tiles array
    // React Query's refetchInterval will poll for new tiles
    qc.setQueryData<Field>(queryKeys.fields.detail(fieldGuid), {
      ...currentField,
      invalidatedTimestamp: Date.now() / 1000,
      tiles: [],
    });
  }

  // Invalidate the query to trigger a refetch
  void qc.invalidateQueries({ queryKey: queryKeys.fields.detail(fieldGuid) });
}

/**
 * Change field visibility in React Query cache.
 * @param update - Object with guid and visibility properties
 * @param queryClient - React Query client instance
 */
export function changeFieldVisibility(
  update: { guid: string; visibility: "none" | "visible" },
  queryClient: QueryClient
): void {
  const currentField = queryClient.getQueryData<Field>(
    queryKeys.fields.detail(update.guid)
  );

  if (currentField) {
    queryClient.setQueryData<Field>(queryKeys.fields.detail(update.guid), {
      ...currentField,
      visibility: update.visibility,
    });
  }
}

/**
 * Change arable field visibility in React Query cache.
 * @param update - Object with guid and visibility properties
 * @param queryClient - React Query client instance
 */
export function changeArableFieldVisibility(
  update: { guid: string; visibility: "none" | "visible" },
  queryClient: QueryClient
): void {
  const currentField = queryClient.getQueryData<ArableField>([
    "arableField",
    update.guid,
  ]);

  if (currentField) {
    queryClient.setQueryData<ArableField>(
      queryKeys.arableFields.detail(update.guid),
      {
        ...currentField,
        visibility: update.visibility,
      }
    );
  }
}

/**
 * Update an arable field via API.
 */
export async function updateArableField(
  field: Omit<ArableField, "geom"> & {
    geom: ArableField["geom"] | FeatureCollection;
  }
): Promise<ArableField> {
  return fetchApi(`fields/${field.guid}`, {
    method: "PUT",
    body: JSON.stringify(field),
  }) as Promise<ArableField>;
}

/**
 * Toggle field editing mode in UI store.
 * Wrapper around the UI store's toggleIsEditing function.
 */
import { useUIStore } from "stores/useUIStore";

export function toggleFieldEditing(): void {
  useUIStore.getState().toggleIsEditing();
}

/**
 * Create a new field via API.
 */
export async function createField(
  field: Partial<Omit<Field, "geom">> & { geom: FeatureCollection }
): Promise<Field> {
  return fetchApi("fields", {
    method: "POST",
    body: JSON.stringify(field),
  }) as Promise<Field>;
}

/**
 * Update an existing field via API.
 */
export async function updateField(
  field: Omit<Field, "geom"> & { geom: FeatureCollection }
): Promise<Field> {
  return fetchApi(`fields/${field.guid}`, {
    method: "PUT",
    body: JSON.stringify(field),
  }) as Promise<Field>;
}

/**
 * Create a new arable field via API.
 */
export async function createArableField(
  field: Partial<Omit<ArableField, "geom">> & {
    geom: FeatureCollection;
    fieldType?: string;
  }
): Promise<ArableField> {
  return fetchApi("fields", {
    method: "POST",
    body: JSON.stringify(field),
  }) as Promise<ArableField>;
}

/**
 * Invalidate arable field data in React Query cache.
 * Sets invalidatedTimestamp and clears tiles array so React Query's refetchInterval
 * will poll for new tiles. Also triggers a refetch.
 * @param fieldGuid - The arable field guid to invalidate
 * @param queryClientParam - React Query client instance (optional - uses global if not provided)
 */
export function invalidateArableFieldData(
  fieldGuid: string,
  queryClientParam?: QueryClient
): void {
  const qc = queryClientParam ?? queryClient;

  const currentField = qc.getQueryData<ArableField>(
    queryKeys.arableFields.detail(fieldGuid)
  );

  if (currentField) {
    // Set invalidatedTimestamp and clear tiles array
    // React Query's refetchInterval will poll for new tiles
    qc.setQueryData<ArableField>(queryKeys.arableFields.detail(fieldGuid), {
      ...currentField,
      invalidatedTimestamp: Date.now() / 1000,
      tiles: [],
    });
  }

  // Invalidate the query to trigger a refetch
  void qc.invalidateQueries({
    queryKey: queryKeys.arableFields.detail(fieldGuid),
  });
}

/**
 * Fetch fields by IDs with result.
 */
export async function fetchFieldsByIdWithResult(
  fieldIds: string[]
): Promise<Field[]> {
  if (!fieldIds || fieldIds.length === 0) {
    return [];
  }
  return fetchApi(`fields?ids=${fieldIds.join(",")}`) as Promise<Field[]>;
}

/**
 * Fetch arable fields by IDs with result.
 */
export async function fetchArableFieldsByIdWithResult(
  fieldIds: string[]
): Promise<ArableField[]> {
  if (!fieldIds || fieldIds.length === 0) {
    return [];
  }
  return fetchApi(`arableFields?ids=${fieldIds.join(",")}`) as Promise<
    ArableField[]
  >;
}

/**
 * Fetch tiles by IDs.
 */
export async function fetchTilesByIds(tileIds: string[]): Promise<unknown[]> {
  if (!tileIds || tileIds.length === 0) {
    return [];
  }
  return fetchApi(`tiles?ids=${tileIds.join(",")}`) as Promise<unknown[]>;
}
